import { FC } from 'react';
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Typography,
  CircularProgress,
  Badge,
  Chip,
} from '@mui/material';
import GroupIcon from '@mui/icons-material/Group';
import { useMessagingContext } from '../../../contexts/messaging.context';
import {
  useGetGroupsHook,
} from '../../../hooks/useMessagingHook';
import { GroupConversation } from '../../../../../../apis/messaging/messaging.interfaces';
import { formatDistanceToNow } from 'date-fns';

const GroupChatList: FC = () => {
  const { state, setSelectedGroup } = useMessagingContext();

  const { data: groupsData, isLoading } = useGetGroupsHook({ page: 1, limit: 50 });
  const groups = groupsData?.groups || [];

  const getTimeAgo = (date?: string): string => {
    if (!date) return '';
    try {
      return formatDistanceToNow(new Date(date), { addSuffix: true });
    } catch {
      return '';
    }
  };

  return (
    <Box
      sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}
      data-testid="group-chat-list"
    >
      <Box
        sx={{
          px: 2,
          py: 1.5,
          borderBottom: 1,
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <GroupIcon fontSize="small" color="primary" />
        <Typography variant="h6" fontWeight={600}>
          Groups
        </Typography>
        {groups.length > 0 && (
          <Chip label={groups.length} size="small" color="primary" variant="outlined" />
        )}
      </Box>

      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : groups.length === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <GroupIcon sx={{ fontSize: 48, color: 'grey.400', mb: 1 }} />
            <Typography variant="body2" color="text.secondary">
              No groups yet
            </Typography>
          </Box>
        ) : (
          <List sx={{ p: 0 }}>
            {groups.map((gc: GroupConversation) => {
              const isSelected = state.selectedGroupId === gc.group._id;
              return (
                <ListItem key={gc.group._id} disablePadding>
                  <ListItemButton
                    selected={isSelected}
                    onClick={() => setSelectedGroup(gc.group._id)}
                    sx={{
                      borderRadius: 1,
                      mx: 1,
                      '&.Mui-selected': {
                        backgroundColor: '#EEF0FF',
                        '&:hover': { backgroundColor: '#E1E4FF' },
                      },
                    }}
                  >
                    <ListItemAvatar>
                      <Badge
                        badgeContent={gc.unreadCount ?? 0}
                        color="primary"
                        invisible={(gc.unreadCount ?? 0) === 0}
                      >
                        <Avatar
                          src={gc.group.avatar_url || ''}
                          sx={{ bgcolor: 'primary.light' }}
                        >
                          <GroupIcon />
                        </Avatar>
                      </Badge>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="subtitle2" fontWeight={500} noWrap>
                            {gc.group.name}
                          </Typography>
                          {gc.lastMessageAt && (
                            <Typography variant="caption" color="text.secondary">
                              {getTimeAgo(gc.lastMessageAt)}
                            </Typography>
                          )}
                        </Box>
                      }
                      secondary={
                        <Typography variant="caption" color="text.secondary">
                          {gc.memberCount} members · {gc.userRole}
                        </Typography>
                      }
                    />
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
        )}
      </Box>
    </Box>
  );
};

export default GroupChatList;
